import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import {
  Typography,
  useTheme,
  Paper,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Skeleton,
  styled,
  Tooltip,
  Checkbox,
} from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { FC, ReactNode, useMemo, useState, useCallback } from 'react';
import { TablePagination } from './TablePagination';
import { ROWS_PER_PAGE_OPTIONS } from './usePagination';
import { EmptyState } from '../EmptyState';
import { SortingOptions } from '../../types';
import { TABLE_LAYOUT_HEIGHT_MARGIN_LARGE, TABLE_LAYOUT_HEIGHT_MARGIN_SMALL } from '../../constants/layout';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { OrderOptionsValues } from '@bandido/shared/enums';

type AlignType = 'right' | 'left' | 'center';

export interface Column<T> {
  id: string;
  label: ReactNode;
  minWidth?: number;
  maxWidth?: number;
  canSort?: boolean;
  sortField?: string;
  align?: AlignType;
  format?: (data: any, item: T) => JSX.Element;
  isSticky?: boolean;
  tooltip?: boolean;
}

export type PaginationProps = {
  rowsPerPage: number;
  page: number;
  handlePagination: (value: number) => void;
  handleRowsPerPage: (value: number) => void;
};

const StyledListWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== 'isLargeUp' })<{ isLargeUp?: boolean }>(
  ({ theme, isLargeUp }) => ({
    position: 'relative',
    height: `calc(100vh - ${isLargeUp ? TABLE_LAYOUT_HEIGHT_MARGIN_LARGE : TABLE_LAYOUT_HEIGHT_MARGIN_SMALL})`,
    overflow: 'hidden',
    boxSizing: 'border-box',
    padding: `${theme.spacing(1)}`,
  }),
);

const StyledTypography = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

type Props<T> = {
  columns: Column<T>[];
  data: {
    key: string;
    items: T[];
    totalCount: number;
  };
  stickyHeader?: boolean;
  onRowSelect?: (value: T) => void;
  selectedItemId?: string | number;
  isLoading?: boolean;
  rowTemplate?: (item: T) => JSX.Element;
  pagination?: PaginationProps;
  sorting?: {
    options: SortingOptions;
    handler: (options: SortingOptions) => void;
  };
  height?: number | string;
  // Checkbox selection props
  enableCheckboxSelection?: boolean;
  selectedItems?: (string | number)[];
  onSelectedItemsChange?: (selectedItems: (string | number)[]) => void;
  checkboxActions?: JSX.Element;
};

export const Table: FC<Props<any>> = ({
  isLoading,
  columns,
  data,
  pagination,
  rowTemplate,
  stickyHeader,
  sorting,
  onRowSelect,
  selectedItemId,
  height,
  enableCheckboxSelection = false,
  selectedItems = [],
  onSelectedItemsChange,
  checkboxActions,
}) => {
  const theme = useTheme();
  const [_, setScrollLeft] = useState(0);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const hasPagination = !!data.items.length && pagination;
  const { isLargeUp } = useBreakpoints();

  // Calculate checkbox column offset if enabled
  const checkboxColumnWidth = 48;
  
  const stickyColumnOffsets = useMemo(() => {
    let leftOffset = enableCheckboxSelection ? checkboxColumnWidth : 0;
    return columns.map((column) => {
      if (column.isSticky) {
        const offset = leftOffset;
        leftOffset += column.minWidth || 120;
        return offset;
      }
      return null;
    });
  }, [columns, enableCheckboxSelection]);

  // Checkbox selection handlers
  const handleSelectAll = useCallback(() => {
    if (!onSelectedItemsChange) return;
    
    const allIds = data.items.map(item => item[data.key]);
    const allSelected = allIds.every(id => selectedItems.includes(id));
    
    if (allSelected) {
      // Deselect all
      onSelectedItemsChange([]);
    } else {
      // Select all
      onSelectedItemsChange(allIds);
    }
  }, [data.items, data.key, selectedItems, onSelectedItemsChange]);

  const handleItemSelect = useCallback((itemId: string | number, index: number, event: React.MouseEvent) => {
    if (!onSelectedItemsChange) return;

    const isShiftPressed = event.shiftKey;
    const isCurrentlySelected = selectedItems.includes(itemId);

    if (isShiftPressed && lastSelectedIndex !== null) {
      // Range selection with Shift+click
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const rangeItems = data.items.slice(start, end + 1).map(item => item[data.key]);
      
      const newSelection = [...selectedItems];
      rangeItems.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      
      onSelectedItemsChange(newSelection);
    } else {
      // Single selection
      if (isCurrentlySelected) {
        onSelectedItemsChange(selectedItems.filter(id => id !== itemId));
      } else {
        onSelectedItemsChange([...selectedItems, itemId]);
      }
      setLastSelectedIndex(index);
    }
  }, [selectedItems, onSelectedItemsChange, lastSelectedIndex, data.items, data.key]);

  const isAllSelected = data.items.length > 0 && data.items.every(item => selectedItems.includes(item[data.key]));
  const isIndeterminate = selectedItems.length > 0 && !isAllSelected;

  return (
    <StyledListWrapper sx={{ height }} isLargeUp={isLargeUp}>
      {isLoading ? (
        <Skeleton variant="rectangular" width="100%" sx={{ borderRadius: 1, height: '400px' }} />
      ) : (
        <>
          {/* Show checkbox actions if enabled and items are selected */}
          {enableCheckboxSelection && selectedItems.length > 0 && checkboxActions && (
            <Box sx={{ mb: 1, p: 1, bgcolor: theme.palette.action.selected, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
                </Typography>
                {checkboxActions}
              </Box>
            </Box>
          )}
          <Paper
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: theme.spacing(2),
              boxShadow: 'none',
              overflow: 'hidden',
              width: '100%',
              height: hasPagination ? 'calc(100% - 50px)' : 'auto',
            }}
          >
            <TableContainer
              sx={{
                width: '100%',
                height: '100%',
                overflow: 'auto',
              }}
            >
              <OverlayScrollbarsComponent
                className="overlayscrollbars-react os-theme-light"
                options={{ scrollbars: { autoHide: 'leave', theme: 'os-theme-light' } }}
                style={{
                  width: '100%',
                  height: '100%',
                  maxHeight: '100%',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                }}
                events={{
                  scroll: (e) => {
                    const { viewport } = e.elements();
                    setScrollLeft(viewport.scrollLeft);
                  },
                }}
                defer
              >
                {data.items && (
                  <MuiTable stickyHeader={stickyHeader} sx={{ minWidth: 'max-content' }}>
                    <TableHead>
                      <TableRow>
                        {/* Checkbox column header */}
                        {enableCheckboxSelection && (
                          <TableCell
                            padding="checkbox"
                            style={{
                              position: 'sticky',
                              top: 0,
                              left: 0,
                              zIndex: 3,
                              width: checkboxColumnWidth,
                              minWidth: checkboxColumnWidth,
                              maxWidth: checkboxColumnWidth,
                              fontWeight: 'bold',
                            }}
                          >
                            <Checkbox
                              checked={isAllSelected}
                              indeterminate={isIndeterminate}
                              onChange={handleSelectAll}
                              color="primary"
                              inputProps={{ 'aria-label': 'select all items' }}
                            />
                          </TableCell>
                        )}
                        {columns.map((column, idx) => (
                          <TableCell
                            key={column.id}
                            style={{
                              position: 'sticky',
                              top: 0,
                              left: column.isSticky ? `${stickyColumnOffsets[idx]}px` : 'unset',
                              zIndex: column.isSticky ? 3 : 2,
                              minWidth: column.minWidth || 100,
                              maxWidth: column.maxWidth || 'none',
                              fontWeight: 'bold',
                            }}
                          >
                            <Box
                              position="relative"
                              display="flex"
                              alignItems="center"
                              sx={{ cursor: column.canSort ? 'pointer' : 'default' }}
                              onClick={() =>
                                column.canSort &&
                                sorting?.handler({
                                  sort: column.sortField ? column.sortField : column.id,
                                  order:
                                    sorting?.options?.order === OrderOptionsValues.asc
                                      ? OrderOptionsValues.desc
                                      : OrderOptionsValues.asc,
                                })
                              }
                            >
                              <Typography
                                sx={{ flex: 1 }}
                                variant="body2"
                                align={column.align || 'center'}
                                color={theme.palette.text.secondary}
                              >
                                <span>{column.label}</span>
                              </Typography>
                              {Boolean(
                                sorting?.options &&
                                column.canSort &&
                                (sorting?.options?.sort === column.id || sorting?.options?.sort === column.sortField),
                              ) && (
                                  <Box sx={{ position: 'relative', top: '4px' }}>
                                    {sorting?.options?.order === OrderOptionsValues.asc ? (
                                      <ArrowDropDown color="primary" />
                                    ) : (
                                      <ArrowDropUp color="primary" />
                                    )}
                                  </Box>
                                )}
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.items.length ? (
                        data.items.map((item, itemIndex) => {
                          if (rowTemplate) {
                            return rowTemplate(item);
                          }
                          const itemId = item[data.key];
                          const isSelected = selectedItems.includes(itemId);
                          
                          return (
                            <TableRow
                              key={itemId}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                  '& td': {
                                    backgroundColor: theme.palette.paper.background,
                                  },
                                },
                                '& td': {
                                  backgroundColor:
                                    item.id === selectedItemId || (enableCheckboxSelection && isSelected)
                                      ? theme.palette.paper.background
                                      : theme.palette.common.white,
                                },
                              }}
                              onClick={onRowSelect ? () => onRowSelect(item) : undefined}
                            >
                              {/* Checkbox column */}
                              {enableCheckboxSelection && (
                                <TableCell
                                  padding="checkbox"
                                  sx={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 2,
                                    width: checkboxColumnWidth,
                                    minWidth: checkboxColumnWidth,
                                    maxWidth: checkboxColumnWidth,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleItemSelect(itemId, itemIndex, e);
                                  }}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    color="primary"
                                    inputProps={{ 'aria-label': `select item ${itemId}` }}
                                  />
                                </TableCell>
                              )}
                              {columns.map((column, idx) => (
                                <TableCell
                                  key={column.id}
                                  align={column.align || 'center'}
                                  sx={{
                                    position: column.isSticky ? 'sticky' : 'relative',
                                    left: column.isSticky ? `${stickyColumnOffsets[idx]}px` : 'unset',
                                    zIndex: column.isSticky ? 2 : 1,
                                    minWidth: column.minWidth || 100,
                                    maxWidth: column.maxWidth || 'none',
                                    whiteSpace: column.tooltip ? 'nowrap' : 'normal',
                                    overflow: column.tooltip ? 'hidden' : 'visible',
                                    textOverflow: column.tooltip ? 'ellipsis' : 'clip',
                                  }}
                                >
                                  {column.tooltip ? (
                                    <Tooltip title={String(item[column.id])}>
                                      <StyledTypography
                                        component="div"
                                        variant="body2"
                                        align={column.align || 'center'}
                                        color={theme.palette.text.secondary}
                                      >
                                        {column.format ? column.format(item[column.id], item) : item[column.id]}
                                      </StyledTypography>
                                    </Tooltip>
                                  ) : (
                                    <Typography
                                      component="div"
                                      variant="body2"
                                      align={column.align || 'center'}
                                      color={theme.palette.text.secondary}
                                    >
                                      {column.format ? column.format(item[column.id], item) : item[column.id]}
                                    </Typography>
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={enableCheckboxSelection ? columns.length + 1 : columns.length}>
                            <EmptyState />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </MuiTable>
                )}
              </OverlayScrollbarsComponent>
            </TableContainer>
          </Paper>
          {hasPagination && (
            <TablePagination
              totalRows={data.totalCount}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              page={pagination.page}
              rowsPerPage={pagination.rowsPerPage}
              onChangePage={pagination.handlePagination}
              onChangeRowsPerPage={pagination.handleRowsPerPage}
            />
          )}
        </>
      )}
    </StyledListWrapper>
  );
};
